import React, { useRef, useMemo, forwardRef, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';
import { uniqBy, isEmpty } from 'lodash';

const BlackduckBinaryAnalysisMappingForm = forwardRef(
  function BlackduckBinaryAnalysisMappingForm(props, ref) {
    const IntRef = useRef({});
    const [form] = Form.useForm();

    // Initialize mapped data for edit scenarios
    const mappedData = useMemo(() => {
      if (props.record) {
        const { groupAppConfig } = props.record;
        if (groupAppConfig) {
          const config = JSON.parse(groupAppConfig || "[]");
          return {
            groups: [{ id: config.id, name: config.name }],
            applications: config.applications || []
          };
        } 
      }
      return { groups: [], applications: [] };
    }, [props.record]);

    const groupsRef = useRef({ groups: mappedData.groups });
    const applicationsRef = useRef({ applications: mappedData.applications });

    // Fetch groups with pagination
    const fetchGroups = useCallback(
      (params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
          props.toolName,
          {
            login_id: props.loginConfigId,
            ...params,
          }
        );
        promise.then((response) => {
          const { projects } = response;
          if (mappedData.groups.length > 0 && !isEmpty(response.projects)) {
            response.projects = uniqBy(
              [...response.projects, ...mappedData.groups],
              "id"
            );
          }

          groupsRef.current.groups = uniqBy(
            [...projects, ...groupsRef.current.groups],
            "id"
          );
        });
        return { promise, cancel };
      },
      [props.toolName, props.loginConfigId, mappedData.groups]
    );

    // Fetch applications with pagination
    const fetchApplications = useCallback(
      (params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
          props.toolName,
          {
            login_id: props.loginConfigId,
            ...params,
          }
        );
        promise.then((response) => {
          const { projects } = response;
          if (mappedData.applications.length > 0 && !isEmpty(response.projects)) {
            response.projects = uniqBy(
              [...response.projects, ...mappedData.applications],
              "id"
            );
          }

          applicationsRef.current.applications = uniqBy(
            [...projects, ...applicationsRef.current.applications],
            "id"
          );
        });
        return { promise, cancel };
      },
      [props.toolName, props.loginConfigId, mappedData.applications]
    );

    // Transform form data to backend format
    const getExtraFieldsData = useCallback((config) => {
      const { groupId, applicationIds } = config;

      const selectedGroup = groupsRef.current.groups.find(g => g.id === groupId);
      const selectedApps = applicationsRef.current.applications.filter(a => applicationIds.includes(a.id));

      const groupAppId = `${groupId}:${applicationIds.join(',')}`;
      const groupAppName = `${selectedGroup.name}:${selectedApps.map(a => a.name).join(',')}`;
      const groupAppConfig = JSON.stringify({
        id: groupId,
        name: selectedGroup.name,
        applications: selectedApps.map(a => ({ id: a.id, name: a.name }))
      });

      return {
        groupAppId,
        groupAppName,
        groupAppConfig,
        ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
      };
    }, []);

    // Parse backend data for form display
    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const parsedConfig = JSON.parse(config.groupAppConfig || "[]");
      return {
        groupId: parsedConfig.id,
        applicationIds: parsedConfig.applications.map(app => app.id),
        ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
        }),
      };
    }, []);

    // Handle group selection change to clear applications
    const onGroupChange = useCallback(
      (value, field) => {
        const { name } = field || {};
        const configurations = form.getFieldValue("configurations");
        const updatedConfiguration = [...configurations];
        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          applicationIds: undefined,
        };
        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form]
    );

    // Define form fields
    const extraFields = useMemo(() => [
      {
        name: "groupId",
        colSpan: "12",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Group",
        props: {
          api: fetchGroups,
          allowClear: true,
          onChange: onGroupChange,
          autoFocus: true,
        },
        required: true,
      },
      {
        name: "applicationIds",
        colSpan: "12",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Application",
        props: {
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations[currentField.name] || {};
            const groupId = currentConfiguration?.groupId;

            if (groupId) {
              const group = groupsRef.current.groups.find((g) => g.id === groupId);
              if (group && !IntRef.current[groupId]) {
                IntRef.current[groupId] = {
                  project: groupId,
                };
              }
            }

            return {
              mode: "multiple",
              api: groupId && fetchApplications,
              allowClear: true,
              apiParams: IntRef.current[groupId],
              resetOptionsProp: IntRef.current[groupId],
              disabled: !groupId,
              placeholder: groupId ? "Select applications" : "Please select a group first"
            };
          },
        },
        required: true,
        help: "Select one or more applications"
      }
    ], [fetchGroups, fetchApplications, onGroupChange, form]);

    return (
      <GenericMappingForm
        ref={ref}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
        {...props}
      />
    );
  }
);

export default BlackduckBinaryAnalysisMappingForm;